import React, { useCallback, useEffect, useRef } from 'react'
import classNames from 'classnames'
import { Modal, Button, List, Icon } from 'antd'
import './AudioHistoryModal.scss'

const formatDuration = seconds => {
  if (seconds == null || seconds === '') return ''

  const totalSeconds = Math.round(Number(seconds))
  if (Number.isNaN(totalSeconds)) return ''

  const minutes = Math.floor(totalSeconds / 60)
  const remainingSeconds = totalSeconds % 60

  if (minutes > 0) {
    return `${minutes}m ${remainingSeconds}s`
  }

  return `${remainingSeconds}s`
}

const formatTimestamp = timestamp => {
  if (!timestamp) return null

  const parsedDate = new Date(timestamp)
  if (Number.isNaN(parsedDate.getTime())) return null

  const date = parsedDate.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  })

  const time = parsedDate.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit'
  })

  return {
    date,
    time,
    summary: `${date} at ${time}`
  }
}

const getVersionCountLabel = count =>
  `${count} version${count === 1 ? '' : 's'}`

const HistoryAudioPlayer = ({ src, onPlay }) => {
  const audioRef = useRef(null)

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    audio.pause()
    audio.currentTime = 0
    audio.load()
  }, [src])

  if (!src) return null

  return (
    <audio
      key={src}
      ref={audioRef}
      controls
      preload="metadata"
      className="audio-history-card__player"
      onPlay={event => onPlay(event.currentTarget)}
    >
      <source src={src} />
      Your browser does not support the audio element.
    </audio>
  )
}

const AudioHistoryModal = ({
  visible,
  history = [],
  currentAudioUrl,
  onCancel,
  onSelect,
  onDelete
}) => {
  const modalBodyRef = useRef(null)

  const stopAllAudio = useCallback(() => {
    if (!modalBodyRef.current) return

    modalBodyRef.current.querySelectorAll('audio').forEach(audio => {
      try {
        audio.pause()
        audio.currentTime = 0
      } catch {
        audio.pause()
      }
    })
  }, [])

  const stopOtherAudio = useCallback(activeAudio => {
    if (!modalBodyRef.current) return

    modalBodyRef.current.querySelectorAll('audio').forEach(audio => {
      if (audio === activeAudio) return

      try {
        audio.pause()
        audio.currentTime = 0
      } catch {
        audio.pause()
      }
    })
  }, [])

  useEffect(() => {
    if (!visible) {
      stopAllAudio()
    }
  }, [visible, stopAllAudio])

  const handleClose = () => {
    stopAllAudio()
    if (onCancel) {
      onCancel()
    }
  }

  const handleSelectAudio = item => {
    Modal.confirm({
      title: 'Restore this audio version?',
      content:
        'This will replace the current scene audio with this saved version while keeping the rest of the history intact.',
      okText: 'Restore Version',
      cancelText: 'Cancel',
      centered: true,
      icon: <Icon type="exclamation-circle" />,
      onOk() {
        onSelect(item)
      }
    })
  }

  const handleDeleteAudio = (item, originalIndex) => {
    Modal.confirm({
      title: 'Delete this audio version?',
      content:
        'This audio version will be permanently removed from history. This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      centered: true,
      icon: <Icon type="exclamation-circle" style={{ color: '#ff4d4f' }} />,
      onOk() {
        if (onDelete) {
          onDelete(originalIndex)
        }
      }
    })
  }

  const reversedHistory = history.slice().reverse()
  const currentVersionIndex = reversedHistory.findIndex(
    item => item.url === currentAudioUrl
  )
  const currentVersionNumber =
    currentVersionIndex >= 0 ? history.length - currentVersionIndex : null

  return (
    <Modal
      maskClosable={false}
      className="audio-history-modal"
      wrapClassName="audio-history-modal-wrap"
      title={
        <div className="audio-history-modal__title">
          <div className="audio-history-modal__title-icon">
            <Icon type="customer-service" />
          </div>
          <div className="audio-history-modal__title-copy">
            <span className="audio-history-modal__title-text">
              Audio History
            </span>
            <span className="audio-history-modal__title-subtext">
              Review, compare, and restore past scene voiceovers.
            </span>
          </div>
          <span className="audio-history-modal__versions-pill">
            {getVersionCountLabel(history.length)}
          </span>
        </div>
      }
      visible={visible}
      centered
      onCancel={handleClose}
      footer={null}
      width={940}
      bodyStyle={{
        padding: 0,
        maxHeight: 'calc(100vh - 40px)',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <div className="audio-history-modal__body" ref={modalBodyRef}>
        <div className="audio-history-modal__shell">
          <section className="audio-history-modal__intro">
            <div className="audio-history-modal__intro-copy">
              <p className="audio-history-modal__intro-text">
                Browse saved versions and restore any previous scene audio
                without losing the rest of the history.
              </p>
            </div>

            <div className="audio-history-modal__intro-meta">
              <div className="audio-history-modal__intro-chip">
                <span>Versions</span>
                <strong>{history.length}</strong>
              </div>

              {currentVersionNumber && (
                <div className="audio-history-modal__intro-chip audio-history-modal__intro-chip--accent">
                  <span>Current</span>
                  <strong>V{currentVersionNumber}</strong>
                </div>
              )}
            </div>
          </section>

          <div className="audio-history-modal__list">
            <List
              itemLayout="horizontal"
              className="audio-history-modal__list-inner"
              dataSource={reversedHistory}
              split={false}
              locale={{
                emptyText: (
                  <div className="audio-history-modal__empty">
                    <div className="audio-history-modal__empty-icon">
                      <Icon type="inbox" />
                    </div>
                    <h4>No saved audio versions yet</h4>
                    <p>
                      Generate scene audio and each version will appear here for
                      playback and quick restore.
                    </p>
                  </div>
                )
              }}
              renderItem={(item, index) => {
                const versionNumber = history.length - index
                const isCurrent = item.url === currentAudioUrl
                const isLatest = index === 0
                const originalIndex = history.length - 1 - index
                const timestampInfo = formatTimestamp(item.timestamp)
                const durationLabel = formatDuration(item.duration)

                const summaryText = timestampInfo
                  ? `Saved ${timestampInfo.summary}`
                  : 'Saved audio snapshot available for restore.'

                return (
                  <List.Item
                    key={
                      item.id || item.url || `audio-version-${versionNumber}`
                    }
                    className={classNames('audio-history-card', {
                      'audio-history-card--current': isCurrent,
                      'audio-history-card--latest': isLatest
                    })}
                  >
                    <div className="audio-history-card__surface">
                      <div className="audio-history-card__header">
                        <div className="audio-history-card__header-copy">
                          <span className="audio-history-card__eyebrow">
                            Audio version
                          </span>
                          <div className="audio-history-card__title-row">
                            <h4>Version {versionNumber}</h4>

                            {isCurrent && (
                              <span className="audio-history-card__status audio-history-card__status--current">
                                Current
                              </span>
                            )}

                            {!isCurrent && isLatest && (
                              <span className="audio-history-card__status audio-history-card__status--latest">
                                Latest
                              </span>
                            )}
                          </div>

                          <p className="audio-history-card__summary">
                            {summaryText}
                          </p>
                        </div>

                        <Button
                          type={isCurrent ? 'default' : 'primary'}
                          icon={isCurrent ? 'check' : 'rollback'}
                          onClick={() => handleSelectAudio(item)}
                          disabled={isCurrent}
                          className={classNames(
                            'audio-history-card__restore-btn',
                            {
                              'audio-history-card__restore-btn--current': isCurrent
                            }
                          )}
                        >
                          {isCurrent ? 'Currently in Use' : 'Restore Version'}
                        </Button>

                        {onDelete && !isCurrent && (
                          <Button
                            type="danger"
                            ghost
                            icon="delete"
                            className="audio-history-card__delete-btn"
                            onClick={() => handleDeleteAudio(item, originalIndex)}
                          >
                            Delete
                          </Button>
                        )}
                      </div>

                      <div className="audio-history-card__content">
                        {(timestampInfo || durationLabel) && (
                          <div className="audio-history-card__meta">
                            {timestampInfo && (
                              <div className="audio-history-card__meta-item">
                                <Icon type="clock-circle" />
                                <span>{timestampInfo.summary}</span>
                              </div>
                            )}

                            {durationLabel && (
                              <div className="audio-history-card__meta-item">
                                <Icon type="customer-service" />
                                <span>{durationLabel}</span>
                              </div>
                            )}
                          </div>
                        )}

                        <div className="audio-history-card__player-shell">
                          <HistoryAudioPlayer
                            src={item.url}
                            onPlay={stopOtherAudio}
                          />
                        </div>
                      </div>
                    </div>
                  </List.Item>
                )
              }}
            />
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default AudioHistoryModal
